import React from "react";
import { logoWhiteUrl } from "../../utils/publicAssets";
import "./OurInstitutionDesign2.css";

const MA_APPLY_URL = "https://admissions.eusbd.eu/esbd-apply/MA-GBSL/";

const stats = [
  { num: "2024", label: "Founded" },
  { num: "Malta", label: "Headquarters" },
  { num: "100%", label: "Online" },
  { num: "4", label: "Faculties" },
  { num: "1", label: "Programme" },
];

const coreValues = [
  {
    name: "Respect",
    desc: "Valuing diverse perspectives & creating an inclusive environment",
    image: "/images/bg/cv-respect.jpg",
  },
  {
    name: "Integrity",
    desc: "Acting with honesty, transparency, & accountability",
    image: "/images/bg/cv-integrity.jpg",
  },
  {
    name: "Innovation",
    desc: "Embracing modern technologies & creative thinking",
    image: "/images/bg/cv-innovation.jpg",
  },
  {
    name: "Excellence",
    desc: "Committing to high standards & continuous improvement",
    image: "/images/bg/cv-excellence.jpg",
  },
  {
    name: "Collaboration",
    desc: "Encouraging teamwork & global partnerships",
    image: "/images/bg/cv-collaboration.jpg",
  },
  {
    name: "Impact",
    desc: "Empowering socially responsible leaders to drive change",
    image: "/images/bg/cv-impact.jpg",
  },
];

const objectives = [
  "Provide high-quality education in leadership, diplomacy, business, and politics through fully online learning models",
  "Prepare students for leadership roles in government, corporate, international organisations, and businesses",
  "Deliver accessible education through practical training, certifications, and structured programmes",
  "Establish academic partnerships with institutions across Europe to create exchange and mobility opportunities",
  "Invest in collaborative learning environments where students can engage in meaningful cross-cultural exchanges",
];

const goals = [
  "Empower global citizens to lead with versatile leadership and diplomatic skills in diverse professional environments",
  "Achieve accredited quality standards and deliver effective, high-quality online programmes across our faculties",
  "Foster international collaborations to provide a global network for students and graduates",
  "Create innovative learning programmes that integrate cutting-edge technology and competency-based approaches",
  "Build strong professional networks with employers and organisations to support graduate career outcomes",
];

const whyChoose = [
  { title: "Strategic Mediterranean Location", text: "Located in Malta, at the crossroads of Europe, Africa, and the Middle East" },
  { title: "Distinguished Faculty & Small Classes", text: "Expert lecturers from diverse global backgrounds and an emphasis on personalised learning" },
  { title: "Internships & Mentoring", text: "Partnerships with international organisations for practical professional experience" },
  { title: "Theory & Practice-Oriented Curriculum", text: "Blending academic knowledge with practical, real-world application" },
  { title: "Study Tours & Exchange Programmes", text: "Enhance your learning with global experiences at partner institutions" },
  { title: "Affordable Tuition & Instalment Plans", text: "Quality education at competitive prices with flexible payment options" },
  { title: "Summer School Programme", text: "Optional immersive summer courses at European partner institutions" },
  { title: "Scholarships Available", text: "Multiple scholarship programmes for students across Africa, Asia, and Europe" },
];

const SmallArrow = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M4 12L12 4M12 4H6M12 4v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function OurInstitution() {
  return (
    <div className="institution-page text-justify">
      {/* HERO */}
      <section className="hero">
        <div
          className="hero__bg"
          style={{
            backgroundImage: "url(/images/bg/institution-hero-bg.jpg)",
          }}
        />
        <div className="hero__ov" />

        <div className="hero__content">
          <img src={logoWhiteUrl} alt="ESBD" className="hero__logo" />
          <h1 className="hero__title">Our Institution</h1>
          <p className="hero__sub">Higher Education &bull; Malta, Europe</p>
        </div>
      </section>

      {/* OUR INSTITUTION */}
      <div className="s">
        <div className="about-row">
          <div className="about-img">
            <img src="/images/bg/institution-brief-history.jpg" alt="ESBD Campus" />
          </div>
          <div className="about-text">
            <h3>Brief History</h3>
            <p>
              The European School of Business &amp; Diplomacy (ESBD) was officially registered on 24 September 2024 under the
              Malta business registry. Our office is located at 341 Tower Business Centre, Toorri, Sliema, Birkirkara, Malta.
            </p>
            <p>
              ESBD was established to meet the growing demands for high-quality leadership, diplomacy, and professional consulting
              education for aspiring business leaders across Africa, Europe, and beyond.
            </p>
          </div>
        </div>

        <div className="about-row about-row--rev">
          <div className="about-text">
            <h3>Who Are We?</h3>
            <p>
              The European School of Business &amp; Diplomacy (ESBD) is a higher education institution committed to developing
              innovative leaders in diplomacy, politics, and business. We stand at the intersection of modern teaching methodologies
              and global partnerships to deliver transformative education.
            </p>
            <p>
              Our programmes are designed to equip students with the strategic, analytical, and leadership competencies required to
              thrive in an interconnected world.
            </p>
          </div>
          <div className="about-img">
            <img src="/images/bg/institution-who-are-we.jpg" alt="Students" />
          </div>
        </div>
      </div>

      {/* STATS BAR */}
      <div className="stats-bar">
        {stats.map((s, i) => (
          <div key={i} className="stat">
            <div className="stat__num">{s.num}</div>
            <div className="stat__label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* VISION & MISSION */}
      <div className="s s--navy">
        <div className="si">
          <div className="sh">
            <h2>Vision &amp; Mission</h2>
          </div>
          <div className="vm-grid">
            <div className="vm-card">
              <h3>Our Vision</h3>
              <p>
                To be a forward-thinking higher education institution recognised for accessible online teaching and learning,
                developing ethical, globally minded leaders who create positive impact in their professions, communities, and wider
                society.
              </p>
            </div>
            <div className="vm-card">
              <h3>Our Mission</h3>
              <p>
                To deliver accessible, high-quality online teaching and learning in leadership, diplomacy, business, and governance,
                equipping diverse learners for responsible professional practice and contributing to society through real-world,
                public-facing education and engagement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CORE VALUES - Flip Cards */}
      <div>
        <div style={{ background: "var(--navy)", padding: "64px 0" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 48px" }}>
            <div className="sh sh--center">
              <h2 style={{ color: "var(--white)" }}>Our Core Values</h2>
              <p style={{ color: "rgba(255,255,255,0.5)" }}>The principles that guide everything we do</p>
            </div>

            <div className="cv-grid">
              {coreValues.map((v, i) => (
                <div key={i} className="cv-flip">
                  <div className="cv-flip__inner">
                    <div className="cv-flip__front">
                      <img src={v.image} alt={v.name} onError={(e) => (e.target.style.display = "none")} />
                      <div className="cv-flip__front-ov" />
                      <div className="cv-flip__label">
                        <span className="cv-flip__name">{v.name}</span>
                        <span className="cv-flip__arrow">
                          <SmallArrow />
                        </span>
                      </div>
                    </div>

                    <div className="cv-flip__back">
                      <p>{v.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* OBJECTIVES & GOALS */}
      <div>
        <div
          style={{
            background: "var(--white)",
            borderTop: "1px solid #F0F0EC",
            borderBottom: "1px solid #F0F0EC",
            padding: "64px 0",
          }}
        >
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 48px" }}>
            <div className="sh">
              <h2>Objectives &amp; Goals</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <div className="og-col">
                <h3>
                  <span />
                  &nbsp;Objectives
                </h3>
                {objectives.map((o, i) => (
                  <div key={i} className="og-item">
                    <div className="og-num">{i + 1}</div>
                    <p>{o}</p>
                  </div>
                ))}
              </div>

              <div className="og-col">
                <h3>
                  <span />
                  &nbsp;Goals
                </h3>
                {goals.map((g, i) => (
                  <div key={i} className="og-item">
                    <div className="og-num" style={{ background: "var(--gold)", color: "var(--navy)" }}>
                      {i + 1}
                    </div>
                    <p>{g}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OUR APPROACH */}
      <div className="s s--navy">
        <div className="si">
          <div className="sh">
            <h2>Our Approach</h2>
            <p>How we deliver transformative education</p>
          </div>
          <div className="ap-grid">
            <div className="ap ap--white">
              <h3>Competency-Based Model</h3>
              <p>
                ESBD employs a competency-based education (CBE) approach, allowing students to progress by mastering specific
                skills. This model enables learners to study at their own pace, focusing on real-world applications and practical
                outcomes.
              </p>
            </div>
            <div className="ap ap--gold">
              <h3>Affordable &amp; Flexible</h3>
              <p>
                Our fully online delivery makes programmes accessible and flexible, offering practical skills modules that enable
                learners to fast-track their journey to a recognised university qualification at competitive prices.
              </p>
            </div>
            <div className="ap ap--white">
              <h3>Teaching Excellence</h3>
              <p>
                We are dedicated to delivering engaging, interactive, and practical education. Our competency-based model ensures
                students gain real-world skills through practical applications, case studies, and hands-on learning experiences.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* WHY CHOOSE ESBD */}
      <div className="s s--alt">
        <div className="si">
          <div className="sh">
            <h2>Why Choose ESBD?</h2>
          </div>
          <div className="wc-grid">
            {whyChoose.map((w, i) => (
              <div key={i} className="wc">
                <div className="wc__dot" />
                <div>
                  <div className="wc__title">{w.title}</div>
                  <div className="wc__text">{w.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="cta">
        <h2>Ready to Join ESBD?</h2>
        <p>Start your journey towards becoming a global leader.</p>
        <a href={MA_APPLY_URL} target="_blank" rel="noreferrer">
          Apply Now <ArrowIcon />
        </a>
      </div>
    </div>
  );
}
